import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { assetPreviewMediaSx } from "./assetConstants";

const PREVIEW_STATUS = {
  idle: "idle",
  checking: "checking",
  ready: "ready",
  unavailable: "unavailable",
};

const AssetPreview = ({ url, loading, horizontal = false, fill = false }) => {
  const [previewStatus, setPreviewStatus] = useState(PREVIEW_STATUS.idle);

  useEffect(() => {
    if (!url?.trim()) {
      setPreviewStatus(PREVIEW_STATUS.idle);
      return undefined;
    }

    setPreviewStatus(PREVIEW_STATUS.checking);
    let cancelled = false;

    const probe = new Image();

    const markUnavailable = () => {
      if (!cancelled) {
        setPreviewStatus(PREVIEW_STATUS.unavailable);
      }
    };

    probe.onload = () => {
      if (cancelled) return;
      if (probe.naturalWidth > 0 && probe.naturalHeight > 0) {
        setPreviewStatus(PREVIEW_STATUS.ready);
      } else {
        markUnavailable();
      }
    };

    probe.onerror = markUnavailable;
    probe.src = url;

    return () => {
      cancelled = true;
      probe.onload = null;
      probe.onerror = null;
    };
  }, [url]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!url) {
    return null;
  }

  const unavailableMessage = (
    <Box sx={{ py: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Preview is not available for this file type.
      </Typography>
    </Box>
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        mt: horizontal || fill ? 0 : 2,
        borderRadius: 1,
        bgcolor: "#fafafa",
        width: fill ? "100%" : "fit-content",
        maxWidth: "100%",
        boxSizing: "border-box",
      }}
    >
      {previewStatus === PREVIEW_STATUS.ready ? (
        <Box
          sx={
            horizontal
              ? { display: "flex", alignItems: "center", gap: 1.5 }
              : undefined
          }
        >
          {horizontal ? null : (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Image preview
            </Typography>
          )}
          <Box
            key={url}
            component="img"
            src={url}
            alt=""
            sx={
              fill
                ? { ...assetPreviewMediaSx, width: "100%", maxWidth: "100%" }
                : assetPreviewMediaSx
            }
          />
        </Box>
      ) : previewStatus === PREVIEW_STATUS.unavailable ? (
        unavailableMessage
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", py: horizontal ? 1 : 3 }}>
          <CircularProgress size={32} />
        </Box>
      )}
    </Paper>
  );
};

export const LocalFilePreview = ({ file }) => {
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (!file || !String(file.type || "").startsWith("image/")) {
      setUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  if (!url) {
    return null;
  }

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1.5, mt: 1.5, borderRadius: 1, bgcolor: "#fafafa", width: "fit-content", maxWidth: "100%" }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Image preview
      </Typography>
      <Box
        component="img"
        src={url}
        alt={file?.name || "Selected image"}
        sx={assetPreviewMediaSx}
      />
    </Paper>
  );
};

export default AssetPreview;
